"use client"

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { ScrollArea } from '@sourcepilot/ui/framework/ui/atoms/scroll-area';
import { usePathname, useSearchParams } from 'next/navigation';
import { Spinner } from '@sourcepilot/ui/framework/ui/atoms/spinner';

import { MessageSquare, PanelRightOpen } from 'lucide-react';
import { Button } from '@sourcepilot/ui/framework/ui/atoms/button';
import { useAtom } from 'jotai'
import { patchStepsAtom, patchThreadsAtom, loadingStateAtom, chatTransitionAtom, useActiveTab, useChatCollapsed } from '@sourcepilot/ui/framework/state/session-data';
import { sessionStorage as spSessionStorage } from '@sourcepilot/ui/framework/data/utils';import { ChatView } from '@sourcepilot/ui/framework/ui/organisms/ChatView';
import { ProjectChatSwitcher } from '@sourcepilot/ui/shell/chat/ProjectChatSwitcher';

// useLayoutEffect runs before the browser paints (so we can read the persisted
// chat width from sessionStorage and apply it before the panel is shown — no
// default-width flash). On the server it would warn, so fall back to useEffect.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Compact floating control shown when the side-panel chat is collapsed. It
 * does NOT reserve column width — the page content reclaims the full width and
 * this just floats at the top-right. Keeps the chat reachable: the project
 * chat switcher (chat icon + project name, a dropdown when the project has
 * multiple chats) plus an expand button that restores the full panel. Off a
 * project route there are no project-scoped chats to switch between, so it
 * falls back to a plain chat chip.
 */
function ChatCollapsedBar({
  isProjectRoute,
  onExpand,
}: {
  isProjectRoute: boolean;
  onExpand: () => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {isProjectRoute ? (
        <ProjectChatSwitcher inline />
      ) : (
        <span className="flex items-center gap-1.5 truncate rounded-md border border-gray-200 bg-white/95 px-2.5 py-1 text-sm text-gray-800 shadow-sm backdrop-blur">
          <MessageSquare className="h-4 w-4 shrink-0 text-gray-400" />
          Chat
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 shrink-0 rounded-md border border-gray-200 bg-white/95 p-1 text-gray-600 shadow-sm backdrop-blur hover:text-gray-900"
        title="Expand chat"
        onClick={onExpand}
      >
        <PanelRightOpen className="h-4 w-4" />
      </Button>
    </div>
  );
}

interface TabContentAreaProps {
  defaultChatWidth?: number;
  children: React.ReactNode;
}

export function TabContentArea({ defaultChatWidth = 500, children }: TabContentAreaProps) {
  // Persisted across reloads (sessionStorage). The component is SSR'd at the
  // default width (no `window` on the server), so we read the stored width in a
  // layout effect (below) — it runs after hydration but BEFORE the browser
  // paints, so the panel never visibly shows the default. Writing happens ONLY
  // on user resize (in handleMouseMove), never via a reactive effect, so the
  // default-valued mount render can't clobber the stored value.
  const [chatWidth, setChatWidth] = useState<number>(defaultChatWidth);
  // Gates the panel render until the stored width is read, so it never paints
  // at the default width first.
  const [chatReady, setChatReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0); // Initialize startX
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const [ , setPatchSteps] = useAtom(patchStepsAtom);
  const [ , setPatchThreads] = useAtom(patchThreadsAtom);
  const [isTransitioning, setIsTransitioning] = useAtom(chatTransitionAtom);

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isChatPage = pathname.includes('/chat/');
  const activeTab = useActiveTab();

  // Get task ID from either query param (?chat=id) or chat page route (/chat/id)
  // Task is the primary DAG context (owns Thread 1:1)
  let taskParam = searchParams.get('chat');
  if (!taskParam && isChatPage) {
    // Extract task ID from /chat/:taskId route
    const match = pathname.match(/\/chat\/([^\/]+)/);
    if (match && match[1] !== 'new') {
      // Don't treat "new" as a task ID - it's the workspace page
      taskParam = match[1];
    }
  }

  // Resolve task class name: tab data first, URL param as fallback (for deep links)
  const taskClassName = activeTab?.taskClassName || searchParams.get('agent') || undefined;

  // Per-chat-id collapse state (persisted in sessionStorage). When collapsed
  // the chat stays in the URL — we just shrink the panel to a compact bar.
  // Collapse only applies in sidebar mode, not on a fullscreen `/chat/` page.
  const [collapsed, setCollapsed] = useChatCollapsed(taskParam);
  const isCollapsed = collapsed && !isChatPage;
  const isProjectRoute = /^\/projects\//.test(pathname);

  // Chat width is persisted PER CHAT (keyed by task id); with no chat in view
  // it falls back to a single shared key. The read effect below re-runs when
  // this key changes, so switching chats restores that chat's own width.
  const chatWidthKey = taskParam ? `chatWidth:${taskParam}` : 'chatWidth';


  const [loadingState, setLoadingState] = useAtom(loadingStateAtom);
  // Mouse event handlers for resizing the chat panel
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    setStartX(e.clientX); // Store initial mouse position
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    const deltaX = startX - e.clientX; // Calculate deltaX based on initial startX
    // Cap the chat width to the full content area: it can be dragged all the
    // way to fullscreen (up to the mini-app area on the left), but never past
    // it — overflowing the container would push the panel off the viewport.
    const container = chatContainerRef.current?.parentElement;
    const maxWidth = container ? container.clientWidth : Infinity;
    const newWidth = Math.min(Math.max(100, chatWidth + deltaX), maxWidth);

    setChatWidth(newWidth);
    // Persist on user resize only (never on the default-valued mount render).
    spSessionStorage.setItem(chatWidthKey, newWidth);
    setStartX(e.clientX); // Update startX for the next mousemove event
  }, [isDragging, startX, chatWidth, chatWidthKey]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Clear loading state after content is loaded
  // useEffect(() => {
  //   // When children change and page is loading, clear the loading state
  //   if (loadingState.type === 'page') {
  //     const timer = setTimeout(() => {
  //       setLoadingState({ type: 'none' });
  //     }, 300); // Reduced timeout for better UX
      
  //     return () => clearTimeout(timer);
  //   }
  // }, [children, loadingState, setLoadingState]);

  // Cleanup loading state when component unmounts
  useEffect(() => {
    return () => {
      setLoadingState({ type: 'none' });
    };
  }, [setLoadingState]);

  // Handle mouse events for dragging
  useEffect(() => {
    if (isDragging) {
      const handleMouseMoveWrapper = (e: MouseEvent) => handleMouseMove(e);
      const handleMouseUpWrapper = () => handleMouseUp();

      window.addEventListener('mousemove', handleMouseMoveWrapper);
      window.addEventListener('mouseup', handleMouseUpWrapper);

      return () => {
        window.removeEventListener('mousemove', handleMouseMoveWrapper);
        window.removeEventListener('mouseup', handleMouseUpWrapper);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Clear patch data when task param changes
  useEffect(() => {
    // task changed, clean websocket chat updates storage
    // (as it will re-fetch again)
    setPatchSteps([]);
    setPatchThreads([]);
  }, [taskParam, setPatchSteps, setPatchThreads]);

  // Trigger animation when transitioning
  const [animatedWidth, setAnimatedWidth] = useState(chatWidth);

  // Apply the persisted width before the first paint (post-hydration), so the
  // SSR default never shows. Sets BOTH chatWidth and animatedWidth (the panel
  // renders from animatedWidth) so neither flashes.
  useIsomorphicLayoutEffect(() => {
    const stored = spSessionStorage.getItem(chatWidthKey, defaultChatWidth);
    setChatWidth(stored);
    setAnimatedWidth(stored);
    setChatReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatWidthKey]);

  useEffect(() => {
    if (isTransitioning === 'shrink') {
      // Fullscreen to sidebar: shrink width from viewport to chatWidth
      setAnimatedWidth(window.innerWidth);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimatedWidth(chatWidth);
        });
      });

      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 650);

      return () => clearTimeout(timer);
    } else if (isTransitioning === 'slide') {
      // Sidebar grow: width from 0 -> chatWidth
      setAnimatedWidth(0);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimatedWidth(chatWidth);
        });
      });

      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 650);

      return () => clearTimeout(timer);
    }
  }, [isTransitioning, setIsTransitioning, chatWidth]);

  // Separate effect to sync width when not transitioning
  useEffect(() => {
    if (!isTransitioning) {
      setAnimatedWidth(chatWidth);
    }
  }, [chatWidth, isTransitioning]);

  return (
    <div className="flex h-[calc(100vh-77px)] relative">
      {/* Content area - always render children but hide visually on chat pages */}
      {/* Children must render on chat pages for redirect logic to work */}
      <ScrollArea
        className="flex-1 min-w-0"
        style={{
          willChange: isTransitioning ? 'contents' : 'auto',
          display: isChatPage && taskParam ? 'none' : 'block'
        }}
      >
        {/* Key by entity ID (not full pathname) to avoid remounting on sub-tab navigation */}
        {/* Extract entity ID from pathname (e.g., /product/123/breakdown -> 123) */}
        <div key={pathname.split('/').filter(Boolean)[1] || pathname} className="h-full">
          {children}
        </div>
      </ScrollArea>
        
      {/* Show loading overlay only for page-level loading */}
        {/* {loadingState.type === 'page' && (
          <div className="absolute inset-0 bg-white/70 dark:bg-gray-800/70 z-50 flex items-center justify-center">
            <div className="text-center bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg">
              <Spinner size="md" />
              <p className="mt-2 font-medium text-gray-700 dark:text-gray-300">
                Loading {loadingState.path?.split('/').slice(-1)[0] || 'page'} content...
              </p>
            </div>
          </div>
        )} */}

      {/* Chat/Console panel - renders when a task exists and the chat isn't
          collapsed. Width determines fullscreen vs sidebar. */}
      {taskParam && !isCollapsed && chatReady && (
        <div
          className="h-full relative overflow-hidden"
          ref={chatContainerRef}
          style={{
            width: isChatPage && !isTransitioning ? '100%' : `${animatedWidth}px`,
            minWidth: isTransitioning ? '0px' : (isChatPage ? '100%' : '300px'),
            maxWidth: isTransitioning ? '100%' : 'none',
            flex: '0 0 auto',
            transition: isTransitioning ? 'all 600ms ease-in-out' : 'none',
            willChange: isDragging || isTransitioning ? 'width' : 'auto',
            contain: isDragging || isTransitioning ? 'layout' : 'none'
          }}
        >
          <div
            className="h-full w-full relative"
            style={{
              userSelect: isDragging ? 'none' : 'auto',
              minWidth: isChatPage ? 'auto' : '300px'
            }}
          >
            {/* Chat component - key forces remount when task changes.
                The per-project chat switcher renders in-flow inside the
                ChatView header (not floating) so it can't overlap the
                header's collapse / new-chat buttons. */}
            {taskParam && <ChatView key={taskParam} taskId={taskParam} taskClassName={taskClassName} isTransitioning={Boolean(isTransitioning)} />}

            {/* Resize handle - only show on non-chat pages */}
            {!isChatPage && (
              <div
                className="absolute left-0 top-0 bottom-0 w-[1px] bg-gray-200 cursor-col-resize transition-all"
                onMouseDown={handleMouseDown}
              >
                <div className="absolute inset-y-0 w-[10px] opacity-0 group-hover:opacity-100 transition-opacity z-50" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Collapsed chat: a floating control at the top-right that reserves NO
          column width, so the page content reclaims the full width. */}
      {taskParam && isCollapsed && (
        <div className="absolute right-3 top-2 z-30">
          <ChatCollapsedBar
            isProjectRoute={isProjectRoute}
            onExpand={() => setCollapsed(false)}
          />
        </div>
      )}

    </div>
  );
}

